<template>
  <div>
    <b-card class="page-card" :style="`${$smallScreen ? 'padding-bottom: 80px;' : ''}`">
      <div class="page-header d-flex justify-content-between">
        <h2>Parts Order {{ partsOrderId }}</h2>
      </div>
      <div v-if="loading" class="text-center py-4">
        <b-spinner></b-spinner>
      </div>
      <div v-else>
        <b-row no-gutters class="vams-summary">
          <div class="mr-4">
            <label class="col-head">Parts Order ID:</label>
            <h4>{{ partsOrder.partsOrderId }}</h4>
          </div>
          <div class="mr-4">
            <label class="col-head">Customer:</label>
            <h4>{{ partsOrder.customerName }}</h4>
          </div>
          <div class="mr-4">
            <label class="col-head">Customer Id:</label>
            <div>
              <h4>{{ partsOrder.customerId }}</h4>
            </div>
          </div>
          <div class="mr-4">
            <label class="col-head">FC Status:</label>
            <h4>{{ frontCounterStatus(partsOrder.posted) }}</h4>
          </div>
          <div class="mr-4">
            <label class="col-head">Total:</label>
            <h4>{{ (partsOrder.subTotal + partsOrder.amtTax) | currency }}</h4>
          </div>
          <div v-show="partsOrder.employeeId !== '999'" class="mr-4">
            <label class="col-head">Sales Person:</label>
            <h4>{{ partsOrder.employeeName }}</h4>
          </div>
          <div v-if="partsOrder.originatedFrom" class="mr-4">
            <label class="col-head">Originated From:</label>
            <h4>{{ partsOrder.originatedFrom }}</h4>
          </div>
        </b-row>

        <b-row class="mt-2">
          <h5>Parts</h5>
        </b-row>
        <b-table
          class="border"
          no-border-collapse
          striped
          sticky-header="63vh"
          :items="filteredPartsOrderItems(partsOrder.partsOrderItems)"
          :fields="fields"
        >
          <template #cell(amtPrice)="{ item }">
            <div>{{ item.amtPrice | currency }}</div>
          </template>
          <template #cell(extendedPrice)="{ item }">
            <div>{{ extendedPrice(item) | currency }}</div>
          </template>
        </b-table>
        <div>
          <b-col sm="5" lg="3" xl="2">
            <b-form-group
              label-cols="6"
              label="Subtotal:"
              label-for="subTotal"
              class="m-0"
              label-class="font-weight-bold"
            >
              <div id="subTotal" class="col-form-label text-left">
                <b>{{ partsOrder.subTotal | currency }}</b>
              </div>
            </b-form-group>
            <b-form-group label-cols="6" label="Sales Tax:" label-for="salesTax" class="m-0">
              <div id="salesTax" class="col-form-label text-left">
                {{ partsOrder.amtTax | currency }}
              </div>
            </b-form-group>
            <b-form-group label-cols="6" label="Total:" label-for="total" class="m-0" label-class="font-weight-bold">
              <div id="total" class="col-form-label text-left">
                <b>{{ (partsOrder.subTotal + partsOrder.amtTax) | currency }}</b>
              </div>
            </b-form-group>
          </b-col>
        </div>
        <div v-show="(partsOrder.shipId || '').toLowerCase() !== 'pickup'" class="mt-4 mb-4">
          <h5>Shipping Info</h5>
          <b-row>
            <b-col>
              {{ partsOrder.shipAddr1 }}
              <br />
              <template v-if="partsOrder.shipAddr2">
                {{ partsOrder.shipAddr2 }}
                <br />
              </template>
              {{ partsOrder.shipCity }}, {{ partsOrder.shipState }} {{ partsOrder.shipPost }}
            </b-col>
          </b-row>
        </div>
        <div style="margin-top: 20px; text-align: left">
          <button class="btn btn-dark" @click="payNow">PAY NOW</button>
        </div>
        <div
          :style="`margin-top: 15px; font-size: 12px; color: #666; text-align: left; ${
            $smallScreen ? 'padding-bottom: 100px; margin-bottom: 16px;' : ''
          }`"
        >
          <p style="margin: 0">
            *This is a summary of your parts order. A full detailed invoice is available upon request and will be
            provided with your receipt.
          </p>
        </div>
      </div>
    </b-card>
  </div>
</template>
<script>
import { mapActions, mapGetters } from 'vuex';
import { GuidLinkActions, GuidLinkGetters } from '@/shared/store/guid-link/types';
import ErrorService from '@/shared/services/ErrorService';

export default {
  name: 'PayPartsOrderPage',
  components: {},
  data() {
    return {
      loading: false,
      notFound: false,
      isInactiveLink: false
    };
  },
  computed: {
    ...mapGetters({
      partsOrder: GuidLinkGetters.GET_PARTS_ORDER,
      paymentLinkData: GuidLinkGetters.GET_PAYMENT_LINK
    }),
    partsOrderId() {
      return this.partsOrder.partsOrderId;
    },
    fields() {
      let fields = [];
      fields.push({ key: 'partNumber', label: 'Part No', sortable: false });
      fields.push({ key: 'partDescription', label: 'Part Description', sortable: false });
      fields.push({ key: 'qtyShip', label: 'Qty Shipped', sortable: false, thStyle: 'width: 45px' });
      fields.push({ key: 'qtyBackorder', label: 'Qty Backorder', sortable: false, thStyle: 'width: 45px' });
      fields.push({ key: 'amtPrice', label: 'Amt Price', sortable: false, thStyle: 'width: 45px' });
      fields.push({ key: 'extendedPrice', label: 'Extended Price', sortable: false, thStyle: 'width: 45px' });

      return fields;
    }
  },
  async created() {
    this.loading = true;
    try {
      await this[GuidLinkActions.FETCH_PARTS_ORDER](this.$route.params.guid);
    } catch (error) {
      let errorMessage = 'Error fetching Parts Order.';
      if (error.response && error.response.status == 415) {
        errorMessage = 'Parts Order Link has been deactivated.';
        this.isInactiveLink = true;
      } else if (error.response && error.response.status == 404) {
        this.notFound = true;
      }
      ErrorService.createErrorToast(this, errorMessage);
    } finally {
      this.loading = false;
    }
  },
  methods: {
    ...mapActions([GuidLinkActions.FETCH_PARTS_ORDER, GuidLinkActions.GET_PAYMENT_LINK]),
    async payNow() {
      try {
        await this[GuidLinkActions.GET_PAYMENT_LINK](this.$route.params.guid);
        const paymentLink = this.paymentLinkData;

        if (!paymentLink) {
          ErrorService.createErrorToast(this, 'Unable to get payment link.');
          return;
        }

        window.location.href = paymentLink;
      } catch (error) {
        ErrorService.createErrorToast(this, 'Error getting payment link.');
      }
    },
    frontCounterStatus(value) {
      if (value == 0) return 'Open';
      else if (value == 1) return 'Invoiced';
    },
    extendedPrice(item) {
      return item.qtyShip * item.amtPrice;
    },
    filteredPartsOrderItems(items) {
      if (!items) {
        return [];
      }
      return items.filter(item => this.extendedPrice(item) !== 0);
    }
  }
};
</script>
